/**
 * FarmerContextRoute — role-aware IDENTITY middleware.
 *
 *   user → FarmerContextRoute → intended component
 *            ├─ CITIZEN: getFarmerprofile() → populate FarmerContext
 *            └─ AGENT  : FarmerDbtVerification (categoryType) → populate FarmerContext
 *
 * The AGENT branch's OTP categoryType is configurable per-route:
 *   - Default `PROFILE_GET` — pure identity verification. Used by
 *     /grievance-redressal and anywhere the resource itself doesn't need
 *     (or have) its own OTP gate. /schemes/details/apply passes its own
 *     `SCHEME_APPLY_PROFILE_GET` instead — same Individual + BankDetails
 *     shape as PROFILE_GET, just a distinct backend categoryType so
 *     scheme-apply's identity check is separable from grievance's.
 *     `NON_DBT_PROFILE_GET` is the tech-support variant that also admits non-DBT farmers.
 *   - Resource-specific (`FARMER_PASSBOOK_GET`, `SOIL_CARD_STATUS_GET`,
 *     ...) — when an AGENT-only resource route used to gate behind a
 *     second OTP after PROFILE_GET, fold that OTP into the middleware
 *     so the AGENT enters their OTP once. Backend returns Individual +
 *     a short-lived `tokenId`; we put both in FarmerContext and the
 *     resource hook skips its inline OTP when it sees the tokenId.
 *
 * For CITIZEN, categoryType is ignored — citizens don't OTP per
 * resource; getFarmerprofile() is always the loader.
 *
 * Place INSIDE a <ProtectedRoute> so unauthenticated visitors never
 * reach this component. Public landing pages (e.g. /soil-lab) must NOT
 * wrap their content in this middleware.
 *
 * See .agent/rewamp/cta/middleware-refactor-plan.md §1.2 (updated).
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";
import { Box, Skeleton, Alert, AlertTitle, ThemeProvider, useTheme } from "@mui/material";
import { centerStageLayoutSx } from "../design/surfaceVocabulary";
import RewampPageShell from "../components/RewampPageShell";
import StateBlock from "../components/sections/StateBlock";
import { FARMER_VERIFY_PAGE_SPEC } from "../pages/specs/farmerVerifyPageSpec";
import { useRewampLocalization, useRewampT } from "../localisation";

import { useDispatch } from "react-redux";

import FarmerDbtVerification from "../../components/FarmerDbtVerification";
import RewampBreadCrumbsBar from "../components/RewampBreadCrumbsBar";
import RewampApplyEligibilityDialog from "../components/dialog/RewampApplyEligibilityDialog";
import {
  dispatchNotification,
  citizenAccess,
  agentAccess,
  getCurrentLanguage,
} from "../../components/Utils";
import { getFarmerprofile } from "../../services/CitizenServices";
import verifyFarmerOtp from "../services/farmerVerification";
import {
  FarmerProvider,
  normaliseIndividual,
  synthesiseFarmerFromResource,
  isProfileIdentityCategoryType,
} from "../context/FarmerContext";
import { SOMETHING_WENT_WRONG } from "../../constants";
import { AppLayoutContext } from "../../contexts/AppLayoutContext";
import generateRewampTheme from "../theme";
import { useRegisterBreadcrumbEntry } from "../contexts/BreadcrumbStackContext";
import { carriedTrail } from "../contexts/useArrivalBreadcrumb";
import {
  accountVerificationBreadcrumbEntry,
  accountVerificationTrailAncestors,
  featureBreadcrumbFromPath,
  homeBreadcrumbEntry,
  schemeVerifyBreadcrumbEntry,
} from "../contexts/breadcrumbStackUtils";
import { FARMER_VERIFY_HEADER } from "../localisation";

const STATUS = Object.freeze({
  LOADING: "loading",
  VERIFYING: "verifying",
  READY: "ready",
  ERROR: "error",
});

// Legacy-chrome column — matches the `.inner-box-screen` pages under DefaultLayout (passbook, soil card, GRM).
const CENTERED_COLUMN_SX = {
  width: { xs: "95%", sm: "91.666%" },
  maxWidth: "1200px",
  mx: "auto",
};

/**
 * Resolve the current user's role for the purposes of this middleware.
 * Returns "CITIZEN", "AGENT", or null if neither (should never happen
 * inside a properly-gated ProtectedRoute — defensive return so the
 * middleware can surface a clean error instead of crashing).
 */
const resolveRole = () => {
  if (citizenAccess()) return "CITIZEN";
  if (agentAccess()) return "AGENT";
  return null;
};

/**
 * @param {object} props
 * @param {string} [props.headerText]
 *   Pass-through for the AGENT verifier's header copy — a
 *   FARMER_VERIFY_HEADER member (see ../localisation/farmerVerifyHeaders.js),
 *   or undefined for the default. Each consumer can override to match its
 *   page (e.g. FARMER_VERIFY_HEADER.FARMER_PASSBOOK).
 * @param {string} [props.categoryType="PROFILE_GET"]
 *   Backend categoryType for the AGENT OTP exchange. When the inner
 *   page is an AGENT-only resource that itself requires an OTP for a
 *   specific categoryType (Passbook → FARMER_PASSBOOK_GET, Soil Card
 *   list → SOIL_CARD_STATUS_GET), pass that here so the middleware
 *   runs ONE OTP instead of two consecutive ones. The resulting
 *   `tokenId` (if any) is exposed via useFarmer().resourceTokenId.
 */
function FarmerContextRoute({ headerText, categoryType = "PROFILE_GET", verifyParent }) {
  useRewampLocalization(["farmerVerify"]);
  const tRewamp = useRewampT();
  const dispatch = useDispatch();
  const location = useLocation();
  const role = useMemo(resolveRole, []);
  // RewampBreadCrumbsBar + FarmerDbtVerification below need rewamp-only
  // palette tokens (primary.interactive, layout.*, neutral.*, ...) that
  // don't exist on the legacy theme — and this middleware is mounted under
  // both RewampLayout AND DefaultLayout (whose <Outlet> is deliberately
  // NOT rewamp-themed, see DefaultLayout.js), so the ambient theme can't be
  // trusted. Same "re-theme one body" move as OtpInputRenderer/hortiWrap.
  const { fontSize, ourTheme } = useContext(AppLayoutContext);
  const rewampTheme = useMemo(() => generateRewampTheme(fontSize, ourTheme), [fontSize, ourTheme]);
  // Chrome follows the mount: under RewampLayout the ambient theme carries layout tokens and the gate
  // renders through RewampPageShell (card geometry, breadcrumb in the gutter — the onboarding shape, no
  // coupling to the onboarding shell). Under DefaultLayout it keeps the legacy centred column so the
  // READY-state page (.inner-box-screen) mounts at the same width.
  const ambientTheme = useTheme();
  const rewampChrome = ambientTheme?.palette?.layout != null;
  // Scheme identity carried in router state by SchemeInfo.js's apply-nav call
  // (same shape SchemePageForm.js reads) — needed so the breadcrumb rendered
  // on this identity-verify screen gets a real "Details" target and correct
  // Apply/current-crumb styling instead of an all-undefined detailsState.
  // This middleware also gates non-scheme routes (grievance-redressal,
  // passbook, soil-card, recommended-schemes, ...) whose router state never
  // carries these fields — `schemeId` presence is what scopes the override
  // below to the scheme-apply flow only, leaving those routes untouched.
  const {
    schemeId,
    level,
    startDate,
    endDate,
    active,
    mainId: mainSchemeId,
  } = location.state || {};
  const schemeRouteState = useMemo(
    () => ({ schemeId, level, startDate, endDate, active, mainSchemeId }),
    [schemeId, level, startDate, endDate, active, mainSchemeId]
  );
  const verifyEntry = useMemo(() => {
    if (schemeId) {
      return schemeVerifyBreadcrumbEntry(schemeRouteState);
    }
    return accountVerificationBreadcrumbEntry({ route: location.pathname });
  }, [schemeId, schemeRouteState, location.pathname]);
  const arrivalTrail = carriedTrail(location.state);
  const verifyAncestors = useMemo(() => {
    const base = (!schemeId && arrivalTrail) || accountVerificationTrailAncestors(location.pathname, schemeRouteState);
    return !schemeId && verifyParent ? [...base, verifyParent] : base;
  }, [schemeId, arrivalTrail, verifyParent, location.pathname, schemeRouteState]);
  // Stay context-transparent (like ProtectedRoute): forward any parent
  // <Outlet context> (e.g. SchemesTabLayout's data/pagination) so a gated child
  // like SchemesRecommendedTab can still read useOutletContext(). Undefined when
  // there's no parent context (e.g. the apply route), which is harmless.
  const parentOutletContext = useOutletContext();

  // CITIZEN starts in LOADING (we'll fetch the profile in an effect);
  // AGENT starts in VERIFYING (we'll render the OTP gate).
  const initialStatus = role === "CITIZEN" ? STATUS.LOADING : STATUS.VERIFYING;

  const [status, setStatus] = useState(initialStatus);
  const [farmer, setFarmer] = useState(null);
  // Resource-scoped tokenId returned by non-PROFILE_GET OTPs. Resource
  // hooks read it via useFarmer().resourceTokenId; null for CITIZEN
  // and for PROFILE_GET (identity-only) routes.
  const [resourceTokenId, setResourceTokenId] = useState(null);
  // Full proxyResponse payload from a resource-categoryType OTP so the
  // inner hook can pluck first-page data without re-fetching.
  const [resourcePayload, setResourcePayload] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const [otpValidationError, setOtpValidationError] = useState({ message: "" });
  const [verifying, setVerifying] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Scheme-apply eligibility block (Class G) surfaced from the identity OTP
  // response itself for AGENT — same dialog/copy CITIZEN gets from
  // checkApplyEligibility (schemeApplyGate.js), just sourced from
  // proxyResponse instead of a separate applyEligibilityCheck call.
  const [eligibilityDialogOpen, setEligibilityDialogOpen] = useState(false);
  const [eligibilityReason, setEligibilityReason] = useState("");
  const language = getCurrentLanguage() === "hi_IN" ? "hi" : "en";
  // When FarmerDbtVerification's OTP screen is showing, this holds its
  // "return to identifier step" handler so the breadcrumb's Back pill can
  // target it instead of navigating away; null on the identifier step
  // (Back then falls through to RewampBreadCrumbsBar's default behaviour).
  const [otpBackHandler, setOtpBackHandler] = useState(null);
  const handleOtpStepChange = useCallback((handler) => {
    setOtpBackHandler(() => handler);
  }, []);

  useRegisterBreadcrumbEntry(verifyEntry, {
    enabled: role === "AGENT" && status === STATUS.VERIFYING,
    ancestors: verifyAncestors,
    exact: !schemeId,
  });

  const featureEntry = useMemo(
    () => (schemeId ? null : featureBreadcrumbFromPath(location.pathname)),
    [schemeId, location.pathname],
  );
  useRegisterBreadcrumbEntry(featureEntry, {
    enabled: role === "AGENT" && status === STATUS.READY && Boolean(featureEntry),
    ancestors: [homeBreadcrumbEntry()],
  });

  /* ── CITIZEN loader ───────────────────────────────────────────────── */
  const loadCitizenProfile = useCallback(async () => {
    setStatus(STATUS.LOADING);
    try {
      const response = await getFarmerprofile();
      // A non-200 is a fetch failure, not a non-DBT farmer. Guard here as the
      // AGENT path does (handleAgentVerify) — otherwise the error body falls
      // through `response.data` into normaliseIndividual (which no longer bails
      // on a missing DBTID) and would render an empty Farmer as READY.
      if (response?.status !== 200) {
        setStatus(STATUS.ERROR);
        return;
      }
      const individual = response?.data?.Individual ?? response?.data;
      const normalised = normaliseIndividual(individual);
      if (!normalised) {
        setStatus(STATUS.ERROR);
        return;
      }
      // BankDetails rides alongside Individual in the PROFILE_GET
      // response (sibling node). normaliseIndividual only maps the
      // identity fields, so attach the raw bank node here — the
      // scheme-apply form reads farmer.bankDetails.
      setFarmer({ ...normalised, bankDetails: response?.data?.BankDetails ?? null });
      setStatus(STATUS.READY);
    } catch (err) {
      dispatchNotification("error", [err?.message || SOMETHING_WENT_WRONG], dispatch);
      setStatus(STATUS.ERROR);
    }
  }, [dispatch]);

  useEffect(() => {
    if (role !== "CITIZEN") return;
    loadCitizenProfile();
  }, [role, reloadKey, loadCitizenProfile]);

  /* ── AGENT verifier ───────────────────────────────────────────────── */
  const handleAgentVerify = useCallback(
    async (otp, identifier = {}) => {
      // The verifier hands back whichever identifier the agent used:
      // { dbtId } (default) or { mobileNumber } (identity categoryTypes
      // only — the mobile option is gated in FarmerDbtVerification).
      const { dbtId, mobileNumber } = identifier;
      setVerifying(true);
      try {
        // Resource-specific OTP payloads ask the backend to bundle the
        // first page of data along with the identity check — Passbook's
        // FARMER_PASSBOOK_GET, for example, includes a SearchCriteria
        // page hint. Use a small `extra` shape per categoryType so the
        // backend doesn't reject the call as malformed.
        const extra =
          categoryType === "FARMER_PASSBOOK_GET"
            ? {
                payload: {
                  SearchCriteria: {
                    dbtId,
                    sortBy: "application_date desc",
                    tenantId: "br",
                    pageNo: 1,
                    pageSize: 10,
                  },
                },
              }
            : categoryType === "SCHEME_APPLY_PROFILE_GET"
              ? { mdmsId: schemeId }
              : {};
        const response = await verifyFarmerOtp({
          dbtId,
          mobileNumber,
          otp,
          categoryType,
          extra,
        });
        if (response?.status !== 200) {
          setOtpValidationError({
            message:
              response?.data?.Errors?.[0]?.code || SOMETHING_WENT_WRONG,
          });
          setOtpValue("");
          return;
        }
        const proxyResponse = response?.data?.proxyResponse;
        // SCHEME_APPLY_PROFILE_GET bundles the CITIZEN-side apply-eligibility
        // check (schemeApplyGate.js) into the identity OTP itself — no
        // Individual node comes back, just {isApplyEnabled, applicationInProgress,
        // message}. Surface the exact same dialog/notification CITIZEN gets
        // instead of falling through to the generic OTP error below.
        if (categoryType === "SCHEME_APPLY_PROFILE_GET") {
          if (proxyResponse?.applicationInProgress) {
            setEligibilityReason(proxyResponse?.message?.[language] || "");
            setEligibilityDialogOpen(true);
            setOtpValue("");
            return;
          }
          if (proxyResponse?.isApplyEnabled === false) {
            dispatchNotification("error", ["schemes.applyEligibilityClosed"], dispatch);
            setOtpValue("");
            return;
          }
        }
        // Two response shapes:
        //   - PROFILE_GET returns an `Individual` node — go through
        //     the normal Farmer normaliser.
        //   - Resource categoryTypes (FARMER_PASSBOOK_GET,
        //     SOIL_CARD_STATUS_GET, …) return resource-flavoured
        //     payloads (Data / Address / SoilTestAnalysisStatuses /
        //     tokenId) WITHOUT Individual. Synthesise a minimal
        //     Farmer from dbtId + Address (+ first soil row if
        //     present) so downstream code stays role/categoryType-
        //     agnostic.
        let normalised = normaliseIndividual(proxyResponse?.Individual);
        if (!normalised && !isProfileIdentityCategoryType(categoryType)) {
          normalised = synthesiseFarmerFromResource(proxyResponse, dbtId);
        }
        if (!normalised) {
          setOtpValidationError({ message: SOMETHING_WENT_WRONG });
          setOtpValue("");
          return;
        }
        // PROFILE_GET proxyResponse carries BankDetails as a sibling of
        // Individual; resource categoryTypes don't, so this is null there.
        setFarmer({ ...normalised, bankDetails: proxyResponse?.BankDetails ?? null });
        // Capture resource-scoped artifacts only when the route asked
        // for one. PROFILE_GET responses don't include either.
        setResourceTokenId(proxyResponse?.tokenId ?? null);
        setResourcePayload(
          isProfileIdentityCategoryType(categoryType) ? null : proxyResponse ?? null,
        );
        setStatus(STATUS.READY);
      } finally {
        setVerifying(false);
      }
    },
    [categoryType, schemeId, language, dispatch],
  );

  /* ── Context API for inner pages ──────────────────────────────────── */
  const refresh = useCallback(async () => {
    if (role === "CITIZEN") {
      setReloadKey((k) => k + 1);
    } else {
      // Force the verifier back on screen — farmer will be re-set on
      // successful OTP. Drop the resource artifacts too; the OTP retry
      // will mint fresh ones.
      setFarmer(null);
      setResourceTokenId(null);
      setResourcePayload(null);
      setOtpValue("");
      setOtpValidationError({ message: "" });
      setStatus(STATUS.VERIFYING);
    }
  }, [role]);

  const invalidate = useCallback(
    (reason) => {
      if (reason) {
        // eslint-disable-next-line no-console
        console.info(`[FarmerContextRoute] identity invalidated: ${reason}`);
      }
      refresh();
    },
    [refresh],
  );

  const contextValue = useMemo(
    () =>
      farmer
        ? { role, farmer, resourceTokenId, resourcePayload, refresh, invalidate }
        : null,
    [role, farmer, resourceTokenId, resourcePayload, refresh, invalidate],
  );

  /* ── Render branches ──────────────────────────────────────────────── */

  // Defensive: shouldn't happen behind ProtectedRoute, but if it does
  // surface a clean message instead of rendering an empty page.
  // Every state the gate renders itself is themed once; chrome (shell vs legacy column) follows the mount.
  const frame = (body, { breadcrumb = false } = {}) => (
    <ThemeProvider theme={rewampTheme}>
      {rewampChrome ? (
        <RewampPageShell spec={FARMER_VERIFY_PAGE_SPEC} breadcrumb={breadcrumb}>
          {body}
        </RewampPageShell>
      ) : (
        <Box sx={centerStageLayoutSx}>
          <Box sx={CENTERED_COLUMN_SX}>{body}</Box>
        </Box>
      )}
    </ThemeProvider>
  );

  if (role == null) {
    return frame(
      <Alert severity="warning">
        <AlertTitle>{tRewamp("FARMER_CONTEXT.UNAUTHORIZED_TITLE")}</AlertTitle>
        {tRewamp("FARMER_CONTEXT.UNAUTHORIZED_NOTICE")}
      </Alert>,
    );
  }

  if (status === STATUS.LOADING) {
    return frame(
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={200} />
      </Box>,
    );
  }

  if (status === STATUS.ERROR) {
    return frame(
      <StateBlock
        kind="error"
        translate={false}
        message={tRewamp("FARMER_CONTEXT.PROFILE_ERROR")}
        onRetry={refresh}
        retryLabel={tRewamp("FARMER_CONTEXT.RETRY")}
      />,
    );
  }

  if (role === "AGENT" && status === STATUS.VERIFYING) {
    const breadcrumbProps = {
      schemeId,
      level,
      startDate,
      endDate,
      active,
      mainSchemeId,
      onBack: otpBackHandler,
    };
    const verifier = (
      <FarmerDbtVerification
        handleVerifyOtp={handleAgentVerify}
        otpValidationError={otpValidationError}
        setOtpValidationError={setOtpValidationError}
        otpValue={otpValue}
        setOtpValue={setOtpValue}
        verificationLoading={verifying}
        headerText={headerText}
        categoryType={categoryType}
        mdmsId={schemeId}
        onOtpStepChange={handleOtpStepChange}
      />
    );
    return (
      <>
        {rewampChrome ? (
          frame(verifier, { breadcrumb: breadcrumbProps })
        ) : (
          <ThemeProvider theme={rewampTheme}>
            <Box sx={centerStageLayoutSx}>
              <Box sx={CENTERED_COLUMN_SX}>
                <RewampBreadCrumbsBar {...breadcrumbProps} sx={{ mb: (theme) => theme.palette.layout.sectionGap }} />
                {verifier}
              </Box>
            </Box>
          </ThemeProvider>
        )}
        <ThemeProvider theme={rewampTheme}>
          <RewampApplyEligibilityDialog
            open={eligibilityDialogOpen}
            onClose={() => {
              setEligibilityDialogOpen(false);
              // Blocked because the OTP already verified an in-progress application — same
              // "return to identifier step" handler the breadcrumb's Back pill uses.
              otpBackHandler?.();
            }}
            reason={eligibilityReason}
          />
        </ThemeProvider>
      </>
    );
  }

  // status === READY → render the protected subtree with context populated.
  const readySubtree = (
    <FarmerProvider value={contextValue}>
      <Outlet context={parentOutletContext} />
    </FarmerProvider>
  );
  // Scheme-apply's v2 form-engine widgets (RewampSelect/RewampDropdown, ...)
  // read rewamp-only theme keys (theme.spacingTokens, palette.neutral, ...)
  // that don't exist on the legacy theme DefaultLayout's <Outlet> deliberately
  // stays on. Every non-scheme consumer of this middleware (grievance-redressal,
  // farmer-passbook, soil-card) renders legacy-themed widgets that must keep
  // inheriting the ambient theme untouched — schemeId (present only on the
  // scheme-apply route, see schemeRouteState above) scopes the re-theme to
  // just that flow instead of blanket-wrapping every route this gate covers.
  return schemeId ? (
    <ThemeProvider theme={rewampTheme}>{readySubtree}</ThemeProvider>
  ) : (
    readySubtree
  );
}

FarmerContextRoute.propTypes = {
  headerText: PropTypes.oneOf(Object.values(FARMER_VERIFY_HEADER)),
  categoryType: PropTypes.string,
  /** Crumb between the arrival trail and the verify step (non-scheme routes); Back lands on it. */
  verifyParent: PropTypes.shape({ id: PropTypes.string, route: PropTypes.string, state: PropTypes.object }),
};

export default FarmerContextRoute;
